// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { renderProductSizes } from 'lib/helper';
import { ProductSizeSelectProps } from 'interfaces';
import { ProductSizesPayload, useFindAllProductSizeSelectorLazyQuery } from 'generated/graphql';

const ProductSizeSelect: FC<ProductSizeSelectProps> = ({
  name,
  title,
  value,
  isActive,
  productType,
  handleChange,
  productTypeId,
  margin = 'dense',
  subProductTypeId,
  isClearable = false,
  ...rest
}) => {
  const [options, setOptions] = useState<ProductSizesPayload['data']>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return renderProductSizes(options);
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllProductSizeSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllProductSize } = data;
      const { data: productSizes, response, pagination } = findAllProductSize || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((productSizes as ProductSizesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllProductSizesInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(productTypeId && { productTypeId }),
            ...(subProductTypeId && { subProductTypeId: subProductTypeId }),
            ...(typeof isActive === 'boolean' && { isActive }),
            ...(productType && { productType }),
          },
        },
      });
    },
    [fetchSelectorData, isActive, subProductTypeId, productTypeId, productType],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default ProductSizeSelect;
